import { Typography, Tooltip, Dropdown, Menu, Tag } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import {
  BTN_TXT,
  GLOBAL_CONSTANTS,
  STATUS,
  DEAL_CATEGORY_COLUMN_FILTERS,
} from "../../../constants/constants";
import { formatCurrency } from "../../../helpers/helpers";
import { safeStringSort } from "../../../helpers/sortHelpers";
import { PERM_CODE } from "../../../constants/PermissionCodes";

import {
  isInvoiceStyleCategory,
  isVenturesCategory,
} from "../../../helpers/dealCategoryProducts";

const { Text } = Typography;

const isInvoiceStyleDeal = (record = {}) =>
  isInvoiceStyleCategory(record?.dealCategoryId) ||
  record?.dealCategoryName === "Invoice Financing" ||
  record?.dealCategoryName === "Supply Chain Financing" ||
  record?.dealCategoryName === "Supply Chain Finance";

const getEnterpriseNameForDeal = (record = {}) => {
  if (!isInvoiceStyleDeal(record)) {
    return null;
  }

  return record?.entName || null;
};

export const getEnqMngtColumns = ({
  onAssign,
  onReject,
  onReopen,
  status,
  onCompleteKYC,
  onSendKycLink,
  onSendQuestionnaire,
  onActivateUser,
  privileges = [],
  onCreditAssessment,
  onViewBusinessDetails,
  onView,
} = {}) => {
  const columns = [
    {
      title: "Business Name",
      dataIndex: "busName",
      key: "busName",
      // width: 250,
      sorter: (a, b) => safeStringSort(a?.busName, b?.busName),
    },
    {
      title: "Deal Category",
      dataIndex: "dealCategoryName",
      key: "dealCategoryName",
      // width: 150,
      filters: DEAL_CATEGORY_COLUMN_FILTERS,
      onFilter: (value, record) => record?.dealCategoryName === value,
      sorter: (a, b) => safeStringSort(a?.dealCategoryName, b?.dealCategoryName),
      render: (text, record) => {
        const categoryName = text || record?.dealCategoryName;

        if (!categoryName) {
          return "N/A";
        }

        const normalized = categoryName.toLowerCase();

        if (normalized.includes("invoice")) {
          return <Tag color="blue">{categoryName}</Tag>;
        }

        if (normalized.includes("supply chain")) {
          return <Tag color="purple">{categoryName}</Tag>;
        }

        if (normalized.includes("joint venture")) {
          return <Tag color="green">{categoryName}</Tag>;
        }

        return <Tag color="default">{categoryName}</Tag>;
      },
    },
    {
      title: "Contact Name",
      dataIndex: "contactName",
      key: "contactName",
      // width: 200,
      sorter: (a, b) => safeStringSort(a?.contactName, b?.contactName),
    },
    {
      title: "Enterprise Name",
      dataIndex: "entName",
      key: "entName",
      width: 250,
      render: (_, record) =>
        getEnterpriseNameForDeal(record) || "-",
      sorter: (a, b) =>
        safeStringSort(getEnterpriseNameForDeal(a), getEnterpriseNameForDeal(b)),
    },
    {
      title: "Business",
      dataIndex: "businessTypeName",
      key: "businessTypeName",
      width: 150,
    },
    {
      title: "Industry",
      dataIndex: "industryTypeName",
      key: "industryTypeName",
      width: 150,
    },
    {
      title: "Annual Turnover",
      dataIndex: "annualTurnoverName",
      key: "annualTurnoverName",
      width: 150,
    },
    {
      title: "Credit Tenure",
      dataIndex: "expectedTenure",
      key: "expectedTenure",
      width: 150,
    },
    {
      title: "Expected Amount",
      dataIndex: "expectedCredit",
      key: "expectedCredit",
      width: 200,
      render: (text) => (text ? formatCurrency(text) : GLOBAL_CONSTANTS.NA),
    },
    {
      title: "Comments",
      dataIndex: "remarks",
      key: "remarks",
      width: 150,
      render: (text) =>
        text ? (
          <Tooltip title={text}>
            <Text ellipsis style={{ maxWidth: 120, display: "block" }}>
              {text}
            </Text>
          </Tooltip>
        ) : (
          GLOBAL_CONSTANTS.NA
        ),
    },
  ];

  // Actions column: All actions rendered as menu items inside Dropdown
  columns.push({
    title: "Actions",
    key: "actions",
    fixed: "right",
    render: (_, record) => {
      const isJV =
        record?.dealCategoryName === "Joint Ventures" ||
        record?.dealCategoryName === "Ventures" ||
        isVenturesCategory(record?.dealCategoryId);
      const menuItems = [];

      const KYC_STATUS_GROUP = [
        STATUS.KYC_PENDING.ID,
        STATUS.KYC_VERIFIED.ID,
        STATUS.KYC_REVIEW.ID,
      ];

      const rowStatus =
        record?.statusId ||
        (Array.isArray(status) ? (status.length === 1 ? status[0] : null) : status);

      const isPending = rowStatus === STATUS.PENDING.ID;
      const isKyc =
        rowStatus === STATUS.KYC_PENDING.ID || KYC_STATUS_GROUP.includes(rowStatus);
      const isActive = rowStatus === STATUS.ACTIVE.ID;
      const isRejected = rowStatus === STATUS.REJECTED.ID;

      // VIEW ACTION (FOR PENDING, KYC_PENDING & REJECTED)
      if (isPending || isKyc || isRejected) {
        menuItems.push({
          key: "view",
          label: BTN_TXT.VIEW,
          onClick: () => onView?.(record),
        });
      }

      // VIEW BUSINESS DETAILS (JV IN KYC_PENDING)
      if (isKyc && isJV && privileges?.includes(PERM_CODE.EYEDT)) {
        menuItems.push({
          key: "viewBusiness",
          label: "View Business Details",
          onClick: () => onViewBusinessDetails?.(record),
        });
      }

      // SEND QUERY (KYC_PENDING)
      if (
        isKyc &&
        (record?.statusName === STATUS.KYC_PENDING.NAME ||
          record?.statusName === STATUS.KYC_REVIEW.NAME ||
          record?.statusName === STATUS.KYC_VERIFIED.NAME) &&
        privileges?.includes(PERM_CODE.EMSQB)
      ) {
        menuItems.push({
          key: "sendQuery",
          label: BTN_TXT.SEND_QUERY,
          onClick: () => onSendQuestionnaire?.(record),
        });
      }

      // COMPLETE KYC (IF only — not JV)
      if (
        isKyc &&
        !isJV &&
        record?.statusName === STATUS.KYC_PENDING.NAME &&
        privileges?.includes(PERM_CODE.EYEDT)
      ) {
        menuItems.push({
          key: "completeKyc",
          label: BTN_TXT.COM_KYC,
          onClick: () => onCompleteKYC?.(record),
        });
      }

      // SEND KYC LINK (IF only — not JV)
      if (
        isKyc &&
        !isJV &&
        record?.statusName === STATUS.KYC_PENDING.NAME &&
        privileges?.includes(PERM_CODE.EYEDT)
      ) {
        menuItems.push({
          key: "sendKycLink",
          label: BTN_TXT.SEND_KYC_LINK,
          onClick: () => onSendKycLink?.(record),
        });
      }

      // ACTIVATE USER (IF only — not JV)
      if (
        isKyc &&
        !isJV &&
        record?.statusName === STATUS.KYC_VERIFIED.NAME &&
        privileges?.includes(PERM_CODE.EYSAC)
      ) {
        menuItems.push({
          key: "activateUser",
          label: BTN_TXT.ACTIVATE_USER,
          onClick: () => onActivateUser?.(record),
        });
      }

      // ASSIGN
      if (isPending && privileges?.includes(PERM_CODE.EQSAP)) {
        menuItems.push({
          key: "assignuser",
          label: BTN_TXT.ASSIGN,
          onClick: () => onAssign?.(record),
        });
      }

      // REJECT — any stage before Activated (including JV / incomplete onboarding)
      const recordStatusId =
        record?.statusId ||
        (Array.isArray(status) ? (status.length === 1 ? status[0] : null) : status);
      const isActivated =
        isActive ||
        recordStatusId === STATUS.ACTIVE.ID ||
        record?.statusName === STATUS.ACTIVE.NAME;
      const isAlreadyRejected =
        isRejected ||
        recordStatusId === STATUS.REJECTED.ID ||
        record?.statusName === STATUS.REJECTED.NAME ||
        status === STATUS.REJECTED.ID;
      const canRejectPending =
        (isPending || status === STATUS.PENDING.ID) && privileges?.includes(PERM_CODE.EQSRJ);
      const canRejectOnboarding =
        (isKyc || status === STATUS.KYC_PENDING.ID) && privileges?.includes(PERM_CODE.EPSRJ);

      if (!isActivated && !isAlreadyRejected && (canRejectPending || canRejectOnboarding)) {
        menuItems.push({
          key: "rejectuser",
          label: BTN_TXT.REJECT,
          onClick: () => onReject?.(record),
        });
      }

      // REOPEN
      if (isRejected && privileges?.includes(PERM_CODE.ERSRO)) {
        menuItems.push({
          key: "reopen",
          label: BTN_TXT.REOPEN,
          onClick: () => onReopen?.(record),
        });
      }

      // SEND QUERY (ACTIVE STATUS - CREDIT ASSESSMENT)
      if (isActive && privileges?.includes(PERM_CODE.EMSQB)) {
        menuItems.push({
          key: "sendQuery",
          label: BTN_TXT.SEND_QUERY,
          onClick: () => onCreditAssessment?.(record),
        });
      }

      if (menuItems.length === 0) return null;

      return (
        <Dropdown
          menu={{ items: menuItems }}
          overlay={<Menu items={menuItems} />}
          trigger={["click"]}
        >
          <MoreOutlined style={{ fontSize: "18px", cursor: "pointer" }} />
        </Dropdown>
      );
    },
  });

  return columns;
};
